import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';


import CustomerHeader from '../../components/ui/CustomerHeader';
import Footer from '../../components/ui/Footer';
import HeroBanner from './components/HeroBanner';
import FeaturedCategories from './components/FeaturedCategories';
import TrendingProducts from './components/TrendingProducts';
import ProductCollection from './components/ProductCollection';
import SalePromoModal from './components/SalePromoModal';
import CustomerTestimonials from './components/CustomerTestimonials';
import ValuePropositions from './components/ValuePropositions';
import ExclusiveOffers from './components/ExclusiveOffers';
import NewsletterSignup from './components/NewsletterSignup';
import SocialProof from './components/SocialProof';

const LandingPage = () => {
  const [cartItemCount] = useState(0);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  return (
    <div className="min-h-screen bg-background">
      <CustomerHeader 
        cartItemCount={cartItemCount}
        onCartClick={handleCartClick}
      />
      <main className="pt-16">
        {/* Hero Section with Parallax */}
        <motion.div 
          style={{ opacity, scale }}
          className="origin-top"
        >
          <HeroBanner />
        </motion.div>

        {/* Featured Categories with Scroll Animation */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <FeaturedCategories />
        </motion.div>

        {/* Value Propositions */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <ValuePropositions />
        </motion.div>

        {/* Trending Products */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <TrendingProducts />
        </motion.div>

        {/* New Arrivals — newest additions (by id desc) */}
        <ProductCollection
          eyebrow="Just In"
          title="New Arrivals"
          subtitle="The freshest bars, oils & care — straight off the cure rack"
          viewAllPath="/product-catalog?filter=new"
          badge="New"
          select={(all) => [...all].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0))}
          max={4}
          altBg
        />

        {/* Best Sellers — featured lineup. Sorted DESC so it never repeats the
            ascending set Trending highlights above. */}
        <ProductCollection
          eyebrow="Customer Favorites"
          title="Best Sellers"
          subtitle="The ones our community keeps coming back for"
          viewAllPath="/product-catalog?filter=bestseller"
          badge="Bestseller"
          select={(all) => all.filter((p) => p?.is_featured).sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0))}
          max={4}
        />

        {/* On Sale — real sale-priced products only (row hides if none).
            Reaching this section triggers the sale-announcement modal (once/session). */}
        <div id="onsale-section">
          <ProductCollection
            eyebrow="Limited Time"
            title="On Sale"
            subtitle="Grab these while they last"
            viewAllPath="/product-catalog?filter=sale"
            badge="Sale"
            select={(all) => all.filter((p) => p?.sale_price_cents != null)}
            max={8}
            altBg
          />
        </div>

        {/* Exclusive Offers with Parallax */}
        <ExclusiveOffers />

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <SocialProof />
        </motion.div>

        {/* Customer Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <CustomerTestimonials />
        </motion.div>

        {/* Newsletter Signup */}
        <NewsletterSignup />
      </main>

      <SalePromoModal targetId="onsale-section" />
      <Footer />
    </div>
  );
};

export default LandingPage;